import { useCallback, useEffect, useRef, useState } from "react"
import { TgMailing } from "@/modules/telegram/interfaces"
import {
  runPagedEnqueue,
  mapEnqueueError,
  createHttpTransport,
  type PagedEnqueueFailure,
  type PagedEnqueuePause,
  type PagedEnqueueTransport,
} from "@/modules/telegram/services/pagedEnqueue"

export type EnqueuePhase =
  | "idle"
  | "uploading"
  | "paused"
  | "success"
  | "failed"

export interface EnqueueUiState {
  phase: EnqueuePhase
  enqueued: number
  errorText: string
  mailingLive: boolean
  resubmitBlocked: boolean
  cancelErrorId: string | null
}

interface PauseControl {
  pause: PagedEnqueuePause
  resolve: () => void
  reject: (reason?: unknown) => void
}

export const usePagedEnqueueRun = (
  transport: PagedEnqueueTransport = createHttpTransport(),
) => {
  const [phase, setPhase] = useState<EnqueuePhase>("idle")
  const [enqueued, setEnqueued] = useState(0)
  const [errorText, setErrorText] = useState("")
  const [mailingLive, setMailingLive] = useState(false)
  const [resubmitBlocked, setResubmitBlocked] = useState(false)
  const [cancelErrorId, setCancelErrorId] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)
  const pauseRef = useRef<PauseControl | null>(null)
  const idRef = useRef<string | null>(null)
  const cancelledOkRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
      pauseRef.current?.reject(new Error("unmounted"))
    }
  }, [])

  // beforeunload-guard на время загрузки/паузы — страховка от случайного закрытия таба.
  useEffect(() => {
    if (phase !== "uploading" && phase !== "paused") return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [phase])

  const applyFailure = (failure: PagedEnqueueFailure) => {
    if (failure.kind === "cancelled-by-user") return // обработка в stop()
    const view = mapEnqueueError(failure)
    setPhase("failed")
    setErrorText(view.text)
    setMailingLive(view.mailingLive)
    setResubmitBlocked(view.mailingLive && !cancelledOkRef.current)
  }

  const run = useCallback(
    ({
      request,
      recipients,
      useMtProto,
    }: {
      request: TgMailing.CreateMailingInitData
      recipients: TgMailing.CallApiBody[]
      useMtProto: boolean
    }) => {
      const controller = new AbortController()
      abortRef.current = controller
      idRef.current = null
      cancelledOkRef.current = false
      setPhase("uploading")
      setEnqueued(0)
      setErrorText("")
      setCancelErrorId(null)
      setMailingLive(false)
      setResubmitBlocked(false)
      void runPagedEnqueue({
        recipients,
        request,
        useMtProto,
        transport,
        signal: controller.signal,
        callbacks: {
          onEvent: (e) => {
            if (e.stage === "init") idRef.current = e.id
            if (e.stage === "pages") setEnqueued(e.enqueued)
          },
          onPause: (pause) =>
            new Promise<void>((resolve, reject) => {
              if (!mountedRef.current) {
                reject(new Error("unmounted"))
                return
              }
              pauseRef.current = { pause, resolve, reject }
              // Текст паузы — из спеки §5 (строка retry-exhausted); kind в failure
              // не превращается: resolve/reject паузы решает оператор.
              setErrorText(
                "Сервер не отвечает. Можно повторить — рассылка продолжится с той же страницы.",
              )
              setPhase("paused")
            }).finally(() => {
              if (pauseRef.current) {
                if (mountedRef.current) setPhase("uploading")
                pauseRef.current = null
              }
            }),
        },
      })
        .then((res) => {
          if (!mountedRef.current) return
          if (res.ok) {
            setPhase("success")
            setMailingLive(false)
            setResubmitBlocked(false)
          } else {
            applyFailure(res.failure)
          }
        })
        // страховка от непредвиденного throw мимо ControlSignal (баг транспорта):
        // без этого — unhandled rejection и форма навсегда в uploading
        .catch((e) => {
          if (!mountedRef.current) return
          const live = idRef.current != null
          setPhase("failed")
          setErrorText(e instanceof Error ? e.message : String(e))
          setMailingLive(live)
          setResubmitBlocked(live)
        })
    },
    [transport],
  )

  const retry = useCallback(() => {
    pauseRef.current?.resolve()
  }, [])

  /** Остановка: при активной паузе — сначала reject (оркестратор завершится
   *  с cancelled-by-user), затем transport.finish/remove. Из failed — просто вызов. */
  const stop = useCallback(
    async (withReport: boolean) => {
      const id = idRef.current
      if (!id) return
      pauseRef.current?.reject(new Error("stopped"))
      try {
        if (withReport) await transport.finish(id)
        else await transport.remove(id)
        cancelledOkRef.current = true
        setPhase("idle")
        setMailingLive(false)
        setResubmitBlocked(false)
        setErrorText("")
        setCancelErrorId(null)
      } catch {
        setPhase("failed")
        setMailingLive(true)
        setResubmitBlocked(true)
        setCancelErrorId(id)
      }
    },
    [transport],
  )

  const clearCancelError = useCallback(() => setCancelErrorId(null), [])

  return {
    phase,
    enqueued,
    errorText,
    mailingLive,
    resubmitBlocked,
    cancelErrorId,
    run,
    retry,
    stop,
    clearCancelError,
  }
}
